import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Paper,
  Box,
  TableHead,
  Table,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useData } from "@pure-ptr/react";
import { submmitClaimData, getClaimStatus} from "../../store/api/claimAnalysis";
import { toast } from "react-toastify";
import { useGlobalStore } from "../../common";
import { Loadable } from "../../common";

export default function ClaimAppealPage(props) {
  const [data, setPatientData] = useState(null);
  const [valueCodes, setValueCodes] = useState(null);
  const [isSubmit, setSubmit] = useState(false);
  const [diagnosisCodes, setDiagnosisCodes] = useState(null);
  const [procedureCodes, setProcedureCodes] = useState([]);  
  const [isLoading, setIsloading] = useState(false);

  const {
    claimData, claimNumber, claimId, actions: { getClaimAllDetails, get, getClaimData }
  } = props;

  const claimDetails = useGlobalStore((state) => state?.claimAnalysis?.detail);
  const claimAllDetails = useGlobalStore((state) => state?.claimAnalysis?.claimAllDetails);
  
  useEffect(() => {
    if (claimData && claimDetails) {
      const latestClaim = claimData?.claims?.reduce((latest, current) => {
        if (!latest) return current;
        return new Date(current.creationdate) > new Date(latest.creationdate)
          ? current
          : latest;
      }, null);

      const chargeData = latestClaim?.claimCharges || [];
      const patientData = {
        patientName: claimAllDetails?.patientFirstname || "",
        patientAge: latestClaim?.age,
        patientStatus: latestClaim?.dischargestatus,
        patientGender: latestClaim?.patientgender === "F" ? "Female"
          : (latestClaim?.patientgender) === "M" ? "Male"
            : "UNKNOWN",
        patientDob: latestClaim?.patientdob,
        patientCity: latestClaim?.patientcity,
        comments: latestClaim?.comments,
        paySource: "",
        patientMrn: latestClaim?.patientMrn,
        patientAccountNumber: latestClaim?.accountNumer,
        fromDate: latestClaim?.fromdate,
        throughDate: latestClaim?.throughdate,
        claimNumber: latestClaim?.claimnumber,
        billType: getBillType(latestClaim?.facilitytype, latestClaim?.frequencytype),
        conditionInformation: latestClaim?.conditionInformation.split(','),
        occurrenceInformation: latestClaim?.occurrenceInformation.split(','),
      };
      setValueCodes({
        valueCode1: latestClaim?.valueCode1 ?? "",
        valueCode2: latestClaim?.valueCode2 ?? "",
        valueCode3: latestClaim?.valueCode3 ?? "",
        valueCode4: latestClaim?.valueCode4 ?? "",
        valueAmount1: latestClaim?.valueAmount1 ?? "",
        valueAmount2: latestClaim?.valueAmount2 ?? "",
        valueAmount3: latestClaim?.valueAmount3 ?? "",
        valueAmount4: latestClaim?.valueAmount4 ?? "",
      });
      setPatientData(patientData);
      const tempRows = chargeData?.map(item => ({
        id: item.id,
        code: item.svcPxcode,
        charge: item.svcChargeamount,
        units: item.svcChargeunits,
        date: item.svcServicedate,
        revenue: item.svcRevenuecode,
        mod1: item.svcMod1,
        mod2: item.svcMod2,
        mod3: item.svcMod3,
        mod4: item.svcMod4,
        principalDx: item.principaldx,
        principalDxDescription: claimDetails.find(x => x.principalDx == item.principaldx)?.principalDxDescription
      }));
      setProcedureCodes(tempRows);
      const diagnosisRows = tempRows.map(item => ({
        code: item?.principalDx,
        desc: item?.principalDxDescription
      }));
      const uniqueDiagnosisCodes = [
        ...new Map(diagnosisRows.map(item => [item.code, item])).values(),
      ];
      setDiagnosisCodes(uniqueDiagnosisCodes || []);
    }
  }, [claimData, claimDetails]);

  const hasConditionData =
  Array.isArray(data?.conditionInformation) &&
  data.conditionInformation.some(item => item?.trim() !== "");

  const hasOccurrenceData =
  Array.isArray(data?.occurrenceInformation) &&
  data.occurrenceInformation.some(item => item?.trim() !== "");

  const hasValueCodeData =
  valueCodes &&
  [1, 2, 3, 4].some((i) => {
    const code = valueCodes[`valueCode${i}` as keyof typeof valueCodes];
    const amount = valueCodes[`valueAmount${i}` as keyof typeof valueCodes];
    return code?.toString().trim() !== "" || amount?.toString().trim() !== "";
  });

  const getBillType = (facType, freqType) => {
    return (
      `${facType ?? ""}${freqType ?? ""
      }`
    ).padStart(5, "0");
  }

  useEffect(() => {
    if (data) return;
    setIsloading(true);
    if (claimNumber && claimId) {
      getClaimAllDetails(claimId);
      getClaimData(claimNumber);
      get(claimId)
    }
    setIsloading(false);
  }, []);

  const {
    data: claimStatus,
    isPending: claimLoading,
  } = useData(() => getClaimStatus(claimId), [claimId]);

  useEffect(() => {
    if (claimStatus) {
      toast.warn(
        "A reconsideration/appeal has already been submitted for this claim.",
        { autoClose: 3000 }
      );
    }
  }, [claimStatus]);

  const {
    reload: runSubmit,
    isPending: isSubmitting,
    error
  } = useData(async () => {
    if (!isSubmit) return null;
    try {
      const res = await submmitClaimData(data?.claimNumber, claimId);
      if (res) {
        toast.info("The claim submission is in progress. You will be notified once it is completed.", {
          autoClose: 3000,
        });
        props.history.push("/cms/claimAnalysis");
      }
    } catch (error) {
      toast.error("Claim submit failed.\n" + error?.payload?.error, {
        autoClose: 3000,
      });
    }
    finally {
      setSubmit(false);
    }
  });

  const handleSubmit = () => {
    setSubmit(true);
    runSubmit();
  }

  const handleCancel = () => {
    props.history.goBack();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return dateString.split("T")[0];
  };
  return (
    (
      <Loadable loading={isLoading || Boolean(isSubmitting) || Boolean(claimLoading)}>
        <Box
          sx={{
            p: { xs: 1, sm: 1.5, md: 2 },
            backgroundColor: "#fff",            
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
              p: 1,
              borderBottom: "1px solid #ccc"
            }}
          >
            <Typography variant="h5" component="div">
              Claim Appeal Details : {data?.claimNumber?data?.claimNumber:""}
            </Typography>            
            <Box
              sx={{
                display: "flex",
                gap: 1,
                width: { xs: "100%", sm: "auto" },
                justifyContent: { xs: "flex-end", sm: "flex-start" }
              }}
            >
              <Button variant="outlined" onClick={handleCancel} sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={claimStatus}
                onClick={handleSubmit} >
                Submit
              </Button>
            </Box>
          </Box>
          {/* ===================== 1. PATIENT INFORMATION ===================== */}
          <Paper sx={{ mb: 1 }}>
            <Box sx={{ p: 1, bgcolor: "#f0f0f0", fontWeight: 500 }}>
              Patient Information
            </Box>

            <Box sx={{ p: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Patient Name"
                    name="patientName"
                    value={data?.patientName || ""}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                    size="small" />
                </Grid>

                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Medical Record #"
                    name="patientMrn"
                    value={data?.patientMrn}
                    InputProps={{ readOnly: true }}
                    size="small" />
                </Grid>

                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Birth Date"
                    name="patientDob"
                    type="date"
                    size="small"
                    value={formatDate(data?.patientDob)}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sex</InputLabel>
                    <Select
                      label="Sex"
                      value={data?.patientGender ?? ""}
                      name="patientGender"
                      readOnly={true}
                    >
                      <MenuItem value="UNKNOWN">UNKNOWN</MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="From Date"
                    name="fromDate"
                    value={formatDate(data?.fromDate)}
                    InputProps={{ readOnly: true }}
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Age(years)"
                    name="age"
                    value={data?.patientAge}
                    InputProps={{ readOnly: true }}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Through Date"
                    name="throughDate"
                    value={formatDate(data?.throughDate)}
                    InputProps={{ readOnly: true }}
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Patient Status"
                    name="patientStatus"
                    value={data?.patientStatus}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                    size="small" />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Bill Type"
                    name="billType"
                    value={data?.billType}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                    size="small" />
                </Grid>
              </Grid>
            </Box>
          </Paper>
          {/* ===================== CONDITION / OCCURRENCE / VALUE CODES ===================== */}
          <Paper sx={{ mb: 1 }}>
            <Box sx={{ p: 1 }}>
              <Grid container spacing={1.5}>
                {/* Condition Codes */}
                <Grid item xs={6} md={3}>
                  <Paper variant="outlined">                    
                    <Box
                      sx={{
                        p: 1,
                        bgcolor: "#f0f0f0",
                        fontWeight: 600,
                        borderBottom: "1px solid #ccc",
                      }}
                    >
                      Condition Codes
                    </Box>
                    <Box sx={{ p: 1, display: "flex", gap: 1, flexWrap: "wrap", minHeight:51.69 }}>                      
                      {hasConditionData ? (
                        data.conditionInformation.map((item, index) => (
                          <TextField
                            key={index}
                            size="small"
                            variant="outlined"
                            value={item || ""}
                            InputProps={{ readOnly: true }}
                            sx={{ width: 50 }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No Data Found
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
                {/* Occurrence Codes */}
                <Grid item xs={6} md={3}>
                  <Paper variant="outlined">
                    <Box
                      sx={{
                        p: 1,
                        bgcolor: "#f0f0f0",
                        fontWeight: 600,
                        borderBottom: "1px solid #ccc",
                      }}
                    >
                      Occurrence Codes
                    </Box>
                    <Box sx={{
                      p: 1, display: "flex", gap: 1,
                      flexWrap: "wrap", minHeight:51.69
                    }}>
                      {hasOccurrenceData ? (
                        data.occurrenceInformation.map((item, index) => (
                          <TextField
                            key={index}
                            size="small"
                            variant="outlined"
                            value={item || ""}
                            InputProps={{ readOnly: true }}
                            sx={{ width: 50 }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No Data Found
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
                {/* Value Codes */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined">
                    <Box
                      sx={{
                        p: 1,
                        bgcolor: "#f0f0f0",
                        fontWeight: 600,
                        borderBottom: "1px solid #ccc",
                      }}
                    >
                      Value Codes
                    </Box>  
                    <Box
                      sx={{
                        p: 1,
                        gap: 1,
                        flexWrap: "nowrap",
                        overflowX: "auto",
                        display: "flex",
                        minHeight:51.69
                      }}
                    >
                      {hasValueCodeData ? (
                        [1, 2, 3, 4].map((i) => (
                          <React.Fragment key={i}>
                            <TextField
                              size="small"
                              variant="outlined"
                              name={`valueCode${i}`}
                              value={
                                valueCodes[`valueCode${i}` as keyof typeof valueCodes] || ""
                              }
                              InputProps={{ readOnly: true }}
                              sx={{ width: 100 }}
                            />
                            <TextField
                              size="small"
                              variant="outlined"
                              name={`valueAmount${i}`}
                              value={
                                valueCodes[`valueAmount${i}` as keyof typeof valueCodes] || ""
                              }
                              InputProps={{ readOnly: true }}
                              sx={{ width: 120 }}
                            />
                          </React.Fragment>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No Data Found
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* ===================== 2. DIAGNOSIS CODES ===================== */}
          <Paper sx={{ mb: 1 }}>
            <Box sx={{ p: 1, bgcolor: "#f0f0f0", fontWeight: 500 }}>
              Diagnosis Codes
            </Box>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {diagnosisCodes && diagnosisCodes.length > 0 ? (
                    diagnosisCodes.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row?.code || ""}</TableCell>
                        <TableCell>{row?.desc || ""}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No content in table
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          {/* ===================== 3. PROCEDURE CODES ===================== */}
          <Paper sx={{ mb: 1}}>
            <Box sx={{ p: 1, bgcolor: "#f0f0f0", fontWeight: 500 }}>
              Procedure Codes
            </Box>

            <Box sx={{ overflowX: "auto", maxHeight:200 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>MOD1</TableCell>
                    <TableCell>MOD2</TableCell>
                    <TableCell>MOD3</TableCell>
                    <TableCell>MOD4</TableCell>
                    <TableCell>Revenue</TableCell>
                    <TableCell>Units</TableCell>
                    <TableCell>Charge</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {procedureCodes && procedureCodes.length > 0 ? (
                    procedureCodes.map((row) => (
                      <TableRow key={row?.id}>
                        <TableCell>{row?.code}</TableCell>
                        <TableCell>{row?.mod1}</TableCell>
                        <TableCell>{row?.mod2}</TableCell>
                        <TableCell>{row?.mod3}</TableCell>
                        <TableCell>{row?.mod4}</TableCell>
                        <TableCell>{row?.revenue}</TableCell>
                        <TableCell>{row?.units}</TableCell>
                        <TableCell>{row?.charge}</TableCell>
                        <TableCell>
                          {row.date
                            ? new Date(row.date).toLocaleDateString()
                            : "-"}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        No content in table
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

            </Box>
          </Paper>
        </Box>
      </Loadable>
    )

  );
}